/**
 * Copy for the destructive confirmations on DevicesPage.
 *
 * Pure and data-only so the wording can be reviewed in one place instead of
 * being scattered across fourteen handlers, and so it can be exercised without
 * mounting the page.
 *
 * Each action returns:
 *   title        — dialog heading
 *   verb         — used in "Are you sure you want to {verb} …?"
 *   noun         — entity word for the bulk lead line ("4 speakers"), so the
 *                  sentence and the title can never name different things
 *   consequence  — what this does to monitoring/alerting. The operational
 *                  warning; rendered as normal body text, not muted.
 *   closing      — reversibility note. Rendered muted and last.
 *
 * The consequence lines are the point of the exercise: an unassigned or deleted
 * device stops being monitored, and staff should not learn that after the fact.
 */

/** Every action id the page can queue. Kept explicit so a typo throws. */
export const DEVICE_ACTIONS = [
  'nc-unassign', 'nc-delete', 'nc-bulk-unassign', 'nc-bulk-delete',
  'wd-unassign', 'wd-delete', 'wd-bulk-unassign', 'wd-bulk-delete',
  'sp-unassign', 'sp-bulk-unassign', 'sp-delete', 'sp-bulk-delete',
  'sd-delete', 'sd-bulk-delete',
  'sv-delete', 'sv-bulk-delete',
  /* Used by DeviceProfilePage and WanderingProfilePage. Shares the nurse
     call / wandering unassign wording deliberately: unassigning from a profile
     and from the list are the same act and should read the same. Its own id so
     the two can diverge later without touching the list copy. */
  'profile-unassign',
];

const NO_UNDO = 'This cannot be undone.';

/* Monitoring loss is the shared theme for the resident-facing tabs (nurse call
   pendants and wandering tags), so both unassign and delete say it. */
const unassignConsequence = (plural) =>
  `Unassigned devices are not monitored by the system and will not generate alerts.`;

const deleteConsequence = (plural) =>
  `${plural ? 'These devices' : 'This device'} will no longer be monitored by the system and ` +
  `will not generate alerts.`;

const pick = (isBulk, count) => ({ isBulk, plural: isBulk ? count !== 1 : false });

/**
 * @param {string} actionId — one of DEVICE_ACTIONS
 * @param {{count?: number}} [opts] — count for bulk actions
 * @returns {{title, verb, confirmLabel, savingLabel, consequence, closing}}
 */
export function describeDeviceAction(actionId, opts = {}) {
  const count = Number(opts.count) || 0;
  const isBulk = actionId.includes('-bulk-');
  const { plural } = pick(isBulk, count);
  const n = (singular) => (isBulk ? `${count} ${singular}${count === 1 ? '' : 's'}` : null);

  switch (actionId) {
    // ---- Nurse Call / Wandering: unassign -----------------------------------
    case 'nc-unassign':
    case 'wd-unassign':
    case 'profile-unassign':
    case 'nc-bulk-unassign':
    case 'wd-bulk-unassign':
      return {
        title: isBulk ? `Unassign ${n('Device')}` : 'Unassign Device',
        noun: 'device',
        verb: 'unassign',
        confirmLabel: 'Unassign',
        savingLabel: 'Unassigning…',
        consequence: unassignConsequence(plural),
        closing:
          `${plural ? 'They' : 'It'} will be deactivated and removed from ${plural ? 'their' : 'its'} ` +
          `assigned area and resident. ${plural ? 'They' : 'It'} will stay in your inventory and can be ` +
          `assigned again later.`,
      };

    // ---- Nurse Call / Wandering: delete -------------------------------------
    case 'nc-delete':
    case 'wd-delete':
    case 'nc-bulk-delete':
    case 'wd-bulk-delete':
      return {
        title: isBulk ? `Delete ${n('Device')}` : 'Delete Device',
        noun: 'device',
        verb: 'delete',
        confirmLabel: 'Delete',
        savingLabel: 'Deleting…',
        consequence: deleteConsequence(plural),
        closing:
          `${plural ? 'They' : 'It'} will be removed from the system entirely and cannot be ` +
          `assigned again. ${NO_UNDO}`,
      };

    // ---- Speakers: unassign -------------------------------------------------
    /* Separate from the nurse call / wandering unassign because the loss is
       different: a speaker isn't monitoring anyone, it's a thing staff call.
       Reversible, so no "cannot be undone". */
    case 'sp-unassign':
    case 'sp-bulk-unassign':
      return {
        title: isBulk ? `Unassign ${n('Speaker')}` : 'Unassign Speaker',
        noun: 'speaker',
        verb: 'unassign',
        confirmLabel: 'Unassign',
        savingLabel: 'Unassigning…',
        consequence: 'Unassigned speakers are not available to be called.',
        closing:
          `${plural ? 'They' : 'It'} will be deactivated and removed from ${plural ? 'their' : 'its'} ` +
          `assigned area and resident. ${plural ? 'They' : 'It'} will stay in your inventory and can be ` +
          `assigned again later.`,
      };

    // ---- Speakers: delete ---------------------------------------------------
    case 'sp-delete':
    case 'sp-bulk-delete':
      return {
        title: isBulk ? `Delete ${n('Speaker')}` : 'Delete Speaker',
        noun: 'speaker',
        verb: 'delete',
        confirmLabel: 'Delete',
        savingLabel: 'Deleting…',
        consequence:
          `${plural ? 'These speakers' : 'This speaker'} will no longer be available to be called.`,
        closing: `${plural ? 'They' : 'It'} will be removed from the system entirely. ${NO_UNDO}`,
      };

    // ---- System devices -----------------------------------------------------
    case 'sd-delete':
    case 'sd-bulk-delete':
      return {
        title: isBulk ? `Delete ${n('System Device')}` : 'Delete System Device',
        noun: 'system device',
        verb: 'delete',
        confirmLabel: 'Delete',
        savingLabel: 'Deleting…',
        consequence:
          `System devices are part of the community's alerting infrastructure. Deleting ` +
          `${plural ? 'them' : 'this device'} may affect the system's ability to create alerts and ` +
          `function correctly throughout the community.`,
        closing: `${NO_UNDO} Only continue if you are certain ${plural ? 'these devices are' : 'this device is'} no longer in use.`,
      };

    // ---- Servers ------------------------------------------------------------
    case 'sv-delete':
    case 'sv-bulk-delete':
      return {
        title: isBulk ? `Delete ${n('Server')}` : 'Delete Server',
        noun: 'server',
        verb: 'delete',
        confirmLabel: 'Delete',
        savingLabel: 'Deleting…',
        consequence:
          `Deleting ${plural ? 'these servers' : 'this server'} will stop the system from functioning ` +
          `or producing any alerts.`,
        closing: `${NO_UNDO} Only continue if you are certain the system is being decommissioned or replaced.`,
      };

    default:
      throw new Error(`describeDeviceAction: unknown action "${actionId}"`);
  }
}
